// @flow

import React, { createContext, useContext, useState, useCallback } from 'react';
import type { Node } from 'react';

import { PRODUCT } from 'Constants/PageType';
import { LIGHT_DARK, PageType } from 'Constants';
import type { LightDark } from 'Constants';

export type PageConfig = {
  product: $Keys<typeof PRODUCT>[],
};

type PageConfigAction = {
  product: $Keys<typeof PRODUCT> | $Keys<typeof PRODUCT>[],
};

type LegalItem = Node | string;

export type HeaderConfig = {
  theme: LightDark,
  noOpacityTransition: boolean,
  toggleHidden: {
    background: boolean,
    logo: boolean,
    nav: boolean,
    ctaButton: boolean,
    ctaButtonAtPageTop: boolean,
    mobileCta: boolean,
    staticLogo: boolean,
  },
};

export type FooterConfig = {
  toggleHidden: {
    icons: boolean,
    companyInfo: boolean,
  },
};

export type FooterLegal = {
  companyName: LegalItem,
  top: LegalItem[],
  numbered: LegalItem[],
  bottom: LegalItem[],
  overwrite: boolean,
};

type FooterLegalAction = {
  companyName?: LegalItem,
  top?: LegalItem | LegalItem[],
  numbered?: LegalItem | LegalItem[],
  bottom?: LegalItem | LegalItem[],
  overwrite?: boolean,
};

type State = {
  page: PageConfig,
  headerConfig: HeaderConfig,
  footerLegal: FooterLegal,
  footerConfig: FooterConfig,
};

const initialState: State = {
  page: {
    product: [PageType.PRODUCT.NONE],
  },
  headerConfig: {
    theme: LIGHT_DARK.LIGHT,
    noOpacityTransition: false,
    toggleHidden: {
      background: false,
      logo: false,
      nav: false,
      ctaButton: false,
      ctaButtonAtPageTop: false,
      mobileCta: false,
      staticLogo: true,
    },
  },
  footerLegal: {
    companyName: null,
    top: [],
    numbered: [],
    bottom: [],
    overwrite: false,
  },
  footerConfig: {
    toggleHidden: {
      icons: false,
      companyInfo: false,
    },
  },
};

const SetterContext: any = createContext();
const StateContext: any = createContext();

const PageContextProvider = ({ children }: { children: Node }) => {
  const [state, setState] = useState(initialState);
  const setPageState = useCallback(
    (data: { page: PageConfigAction, headerConfig: HeaderConfig, footerLegal: FooterLegalAction, footerConfig: FooterConfig }) => {
      const {
        page: { product },
        headerConfig: {
          toggleHidden: headerToggleHidden = {},
          theme = initialState.headerConfig.theme,
          noOpacityTransition = initialState.headerConfig.noOpacityTransition,
        },
        footerLegal: { companyName = null, top = [], numbered = [], bottom = [], overwrite = false },
        footerConfig: { toggleHidden = {} },
      } = data;
      let page: any;
      if (Array.isArray(product)) {
        if (product.includes(PageType.PRODUCT.NONE)) {
          throw new Error(`PageConfig.product cannot be an array with ${product.join(' & ')}`);
        } else page = { product };
      } else {
        page = {
          product: [product],
        };
      }
      setState({
        page,
        headerConfig: {
          toggleHidden: {
            ...initialState.headerConfig.toggleHidden,
            ...headerToggleHidden,
          },
          theme,
          noOpacityTransition,
        },
        footerLegal: {
          companyName,
          top: Array.isArray(top) ? top : [top],
          numbered: Array.isArray(numbered) ? numbered : [numbered],
          bottom: Array.isArray(bottom) ? bottom : [bottom],
          overwrite,
        },
        footerConfig: {
          toggleHidden: {
            ...initialState.footerConfig.toggleHidden,
            ...toggleHidden,
          },
        },
      });
    },
    [setState]
  );
  return (
    <StateContext.Provider value={state}>
      <SetterContext.Provider value={setPageState}>{children}</SetterContext.Provider>
    </StateContext.Provider>
  );
};

const useSetPageState = () => useContext(SetterContext);
const usePageState = (): State => useContext(StateContext);

export { PageContextProvider, useSetPageState, usePageState };
